import logger from 'src/utils/logger';

/**
 * Single source of truth for the access token.
 *
 * Every read and every write of the JWT goes through this module so that:
 *  - no code path can delete a token that a *different* request was issued with
 *    (see `handleUnauthorized`) — that used to wipe a freshly issued token when
 *    a late response from the previous session came back with 401,
 *  - React can subscribe to token changes instead of polling `localStorage`,
 *  - a logout in another browser tab is picked up through the `storage` event.
 *
 * Intentionally free of app dependencies (no react, no query client, no router)
 * so that every service can import it without creating a cycle.
 */
export const ACCESS_TOKEN_KEY = 'access_token';

type Listener = () => void;

const listeners = new Set<Listener>();

const notify = () => {
  listeners.forEach(listener => {
    try {
      listener();
    } catch (error) {
      logger.error('Auth token listener failed', error);
    }
  });
};

/** Current access token, or `null` when there is none (or storage is unavailable). */
export const getToken = (): string | null => {
  try {
    return localStorage.getItem(ACCESS_TOKEN_KEY);
  } catch (error) {
    logger.warn('localStorage is not available');
    return null;
  }
};

/** Thrown by `setToken` when the token cannot be written to `localStorage`. */
export class TokenPersistenceError extends Error {
  constructor() {
    super('Failed to persist the access token');
    this.name = 'TokenPersistenceError';
  }
}

/**
 * Persists the token.
 *
 * Throws `TokenPersistenceError` if storage refuses the write (private mode,
 * storage disabled, quota exhausted) — and deliberately does *not* `notify()`
 * in that case. Swallowing the error was worse than the failure itself: the
 * login reported success, the app navigated to `/dashboard`, `<Auth>` read
 * `null` back and bounced to `/login` — with no message, forever.
 */
export const setToken = (token: string): void => {
  try {
    localStorage.setItem(ACCESS_TOKEN_KEY, token);
  } catch (error) {
    logger.error('Failed to persist access token', error);
    throw new TokenPersistenceError();
  }
  notify();
};

export const clearToken = (): void => {
  try {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
  } catch (error) {
    logger.error('Failed to remove access token', error);
  }
  notify();
};

/**
 * Removes the token, but only if storage still holds exactly the token the
 * caller's request was sent with.
 *
 * This is the guard against token stomping: a request that was started before a
 * logout (or before a re-login) can answer with 401 seconds later, long after a
 * new, valid token has been written. Deleting it there logs the user straight
 * back out — the classic "login works, then bounces back to /login".
 *
 * @param tokenUsedForRequest the token that was put into the request's
 *        Authorization header.
 * @returns whether the token was actually removed.
 */
export const clearTokenIfCurrent = (tokenUsedForRequest?: string | null): boolean => {
  if (!tokenUsedForRequest) return false;
  if (getToken() !== tokenUsedForRequest) {
    logger.warn('Ignoring 401 for a token that is no longer the current one');
    return false;
  }
  clearToken();
  return true;
};

/**
 * Shared 401 handler for the raw-`fetch` services.
 * Call with the token the failing request used.
 */
export const handleUnauthorized = (tokenUsedForRequest?: string | null): boolean =>
  clearTokenIfCurrent(tokenUsedForRequest);

/**
 * Subscribes to token changes. Returns the unsubscribe function.
 * Fires for same-document changes (`setToken` / `clearToken`) and for changes
 * made by another tab (`storage` event).
 */
export const subscribe = (listener: Listener): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

/** Marker used by the auth middleware for "request was never sent, no token". */
export const UNAUTHENTICATED_ERROR = 'unauthenticated:no_access_token';

/**
 * Response the auth middleware returns instead of firing a request without a
 * token. openapi-fetch short-circuits as soon as `onRequest` returns a
 * `Response`, so nothing leaves the browser.
 */
export const createUnauthenticatedResponse = (): Response =>
  new Response(JSON.stringify({ detail: UNAUTHENTICATED_ERROR }), {
    status: 401,
    statusText: 'Unauthorized',
    headers: { 'Content-Type': 'application/json' },
  });

/** True for the error body produced by `createUnauthenticatedResponse`. */
export const isUnauthenticatedError = (error: unknown): boolean =>
  typeof error === 'object' && error !== null && (error as { detail?: unknown }).detail === UNAUTHENTICATED_ERROR;

if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
  window.addEventListener('storage', event => {
    // event.key === null means the whole storage was cleared.
    if (event.key !== null && event.key !== ACCESS_TOKEN_KEY) return;
    if (event.storageArea && event.storageArea !== window.localStorage) return;
    notify();
  });
}
